// ADR-198: Reports → Scheduled Jobs — live overview, refreshed while the page is open.
import { useCallback, useEffect, useRef, useState } from 'react';
import { scheduledJobsApi } from '../services/api/scheduledJobsApi';
import { SCHEDULED_JOBS_REFRESH_MS } from '../constants/scheduledJobs';

export const useScheduledJobs = ({ refreshMs = SCHEDULED_JOBS_REFRESH_MS } = {}) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const mounted = useRef(true);

  const load = useCallback(async ({ background = false } = {}) => {
    if (background) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const result = await scheduledJobsApi.getOverview();
      if (!mounted.current) return;
      setData(result);
      setError(null);
      setLoadedAt(new Date());
    } catch (err) {
      if (!mounted.current) return;
      setError(err);
    } finally {
      if (mounted.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const timer = refreshMs > 0 ? setInterval(() => load({ background: true }), refreshMs) : null;
    return () => {
      mounted.current = false;
      if (timer) clearInterval(timer);
    };
  }, [load, refreshMs]);

  const refetch = useCallback(() => load({ background: true }), [load]);

  return { data, isLoading, isRefreshing, error, loadedAt, refetch };
};
